import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

/**
 * Hero 3D do Dossiê — peça de destaque, bespoke (não reaproveita o HUD do Pessoal).
 *  • card com parallax-tilt que segue o mouse (perspectiva real, camadas em translateZ);
 *  • orb 3D do score: esfera com sombreamento radial, brilho especular, anel de
 *    progresso (conic-gradient) e glow ambiente;
 *  • chips de KPI que "flutuam" à frente (translateZ alto);
 *  • equipe como constelação de esferas glossy (1 por colaborador, cor por gênero) —
 *    representa o headcount em 3D sem ser "boneco".
 * Degrada bem: sem score, sem equipe ou sem stats, ainda renderiza.
 */

interface GeneroQtd {
  sexo: string;
  qtd: number;
}
interface StatHero {
  label: string;
  value: string;
}
interface DossieHero3DProps {
  nome: string;
  idLinha: string;
  periodoLabel: string;
  score?: number;
  classif?: string;
  colaboradores: number;
  porGenero: GeneroQtd[];
  stats: StatHero[];
}

const corGenero = (sexo: string): string => {
  const s = (sexo || '').toLowerCase();
  if (s.startsWith('f')) return '#F472B6';
  if (s.startsWith('m')) return '#38BDF8';
  return '#A78BFA';
};

const corScore = (p: number): string => {
  if (p >= 80) return '#34D399';
  if (p >= 50) return '#FBBF24';
  return '#FB7185';
};

/** Esferinha glossy reutilizada no cluster de equipe (medalhão 3D minúsculo). */
function MiniOrb({ color, z, delay }: { color: string; z: number; delay: number }) {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay, type: 'spring', stiffness: 260, damping: 18 }}
      style={{
        width: '18px',
        height: '18px',
        borderRadius: '999px',
        flexShrink: 0,
        transform: `translateZ(${z}px)`,
        background: `radial-gradient(circle at 32% 26%, #ffffff, ${color} 46%, rgba(0,0,0,0.55) 130%)`,
        boxShadow: `0 4px 10px -2px ${color}aa, inset 0 1px 2px rgba(255,255,255,0.7)`,
      }}
    />
  );
}

function ScoreOrb({ score, classif }: { score?: number; classif?: string }) {
  const p = Math.max(0, Math.min(100, score ?? 0));
  const c = corScore(p);
  return (
    <Box
      position="relative"
      w="158px"
      h="158px"
      flexShrink={0}
      style={{ transform: 'translateZ(70px)' }}
    >
      {/* glow ambiente */}
      <Box
        position="absolute"
        inset="-22%"
        borderRadius="full"
        style={{ background: c, filter: 'blur(34px)', opacity: 0.4 }}
      />
      {/* anel de progresso */}
      <Box
        position="absolute"
        inset="0"
        borderRadius="full"
        style={{ background: `conic-gradient(${c} ${p * 3.6}deg, rgba(255,255,255,0.10) 0deg)` }}
      />
      {/* esfera */}
      <Box
        position="absolute"
        inset="12px"
        borderRadius="full"
        style={{
          background: 'radial-gradient(circle at 34% 27%, #ffffff, #2D9CFF 36%, #0A3A6E 100%)',
          boxShadow:
            'inset 0 -12px 32px rgba(0,0,0,0.45), inset 0 8px 16px rgba(255,255,255,0.55), 0 22px 48px -14px rgba(0,0,0,0.6)',
        }}
      >
        {/* brilho especular */}
        <Box
          position="absolute"
          top="13%"
          left="22%"
          w="36%"
          h="24%"
          borderRadius="full"
          style={{ background: 'rgba(255,255,255,0.85)', filter: 'blur(7px)' }}
        />
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="100%"
          color="white"
          position="relative"
        >
          <Text
            fontSize="46px"
            fontWeight={900}
            lineHeight="1"
            letterSpacing="-2px"
            textShadow="0 2px 10px rgba(0,0,0,0.45)"
          >
            {Math.round(p)}
          </Text>
          <Text fontSize="9.5px" fontWeight={800} letterSpacing="1.6px" opacity={0.9}>
            {(classif ?? 'SCORE').toUpperCase()}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}

export function DossieHero3D({
  nome,
  idLinha,
  periodoLabel,
  score,
  classif,
  colaboradores,
  porGenero,
  stats,
}: DossieHero3DProps) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), {
    stiffness: 140,
    damping: 14,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 140,
    damping: 14,
  });

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  // Cluster: 1 esferinha por colaborador (limite visual), distribuída por gênero.
  const CAP = 28;
  const totalGen = porGenero.reduce((s, g) => s + g.qtd, 0) || colaboradores;
  const orbs: string[] = [];
  if (totalGen > 0) {
    porGenero.forEach(g => {
      const n = Math.round((g.qtd / totalGen) * Math.min(totalGen, CAP));
      for (let i = 0; i < n; i += 1) orbs.push(corGenero(g.sexo));
    });
  }
  while (orbs.length < Math.min(colaboradores, CAP)) orbs.push('#A78BFA');
  const overflow = Math.max(0, colaboradores - orbs.length);

  return (
    <Box style={{ perspective: '1300px' }} mb={6} onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div style={{ rotateX, rotateY, transformStyle: 'preserve-3d', borderRadius: '26px' }}>
        <Box
          position="relative"
          borderRadius="26px"
          p={{ base: 6, md: 8 }}
          boxShadow="0 34px 90px -34px rgba(6,18,40,0.85)"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {/* Superfície clipada (gradiente + mesh + grade) — flat em translateZ(0),
              fora do contexto 3D do conteúdo, p/ o overflow não achatar a profundidade */}
          <Box
            position="absolute"
            inset="0"
            borderRadius="26px"
            overflow="hidden"
            bgGradient="linear(135deg, #0A1B38 0%, #123E78 52%, #0B2A55 100%)"
            style={{ transform: 'translateZ(0)' }}
          >
            <Box
              position="absolute"
              top="-30%"
              right="-10%"
              w="60%"
              h="120%"
              style={{
                background: 'radial-gradient(circle, rgba(56,189,248,0.28), transparent 60%)',
                filter: 'blur(20px)',
              }}
            />
            <Box
              position="absolute"
              bottom="-40%"
              left="-10%"
              w="55%"
              h="120%"
              style={{
                background: 'radial-gradient(circle, rgba(124,92,224,0.26), transparent 60%)',
                filter: 'blur(20px)',
              }}
            />
            <Box
              position="absolute"
              inset="0"
              opacity={0.06}
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
                backgroundSize: '34px 34px',
              }}
            />
          </Box>

          <Flex
            position="relative"
            gap={6}
            align="center"
            justify="space-between"
            wrap={{ base: 'wrap', md: 'nowrap' }}
          >
            {/* Identidade + chips flutuantes */}
            <Box flex={1} minW="240px" color="white" style={{ transform: 'translateZ(35px)' }}>
              <Text fontSize="9px" fontWeight={800} letterSpacing="2.4px" opacity={0.7} mb={1}>
                DOSSIÊ 360 · AUDITORIA INTELIGENTE
              </Text>
              <Text
                fontSize={{ base: '24px', md: '30px' }}
                fontWeight={900}
                letterSpacing="-0.8px"
                lineHeight="1.05"
                noOfLines={2}
              >
                {nome}
              </Text>
              <Text fontSize="12px" opacity={0.78} mt={2}>
                {idLinha}
              </Text>

              <Flex gap={3} mt={5} wrap="wrap" style={{ transform: 'translateZ(75px)' }}>
                {stats.map(s => (
                  <Box
                    key={s.label}
                    px={4}
                    py={2.5}
                    borderRadius="14px"
                    style={{
                      background: 'rgba(255,255,255,0.10)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      boxShadow: '0 12px 26px -14px rgba(0,0,0,0.6)',
                    }}
                  >
                    <Text
                      fontSize="9px"
                      fontWeight={800}
                      letterSpacing="0.6px"
                      opacity={0.7}
                      textTransform="uppercase"
                      color="white"
                    >
                      {s.label}
                    </Text>
                    <Text
                      fontSize="17px"
                      fontWeight={800}
                      color="white"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {s.value}
                    </Text>
                  </Box>
                ))}
              </Flex>

              {/* Equipe — constelação de esferas (1 por colaborador) */}
              {colaboradores > 0 && (
                <Box mt={6} style={{ transform: 'translateZ(55px)' }}>
                  <Flex align="center" gap={2} mb={2}>
                    <Text fontSize="28px" fontWeight={900} color="white" lineHeight="1">
                      {colaboradores}
                    </Text>
                    <Text
                      fontSize="11px"
                      fontWeight={700}
                      color="white"
                      opacity={0.75}
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      colaborador{colaboradores === 1 ? '' : 'es'}
                    </Text>
                  </Flex>
                  <Flex gap="6px" wrap="wrap" maxW="380px" align="center">
                    {orbs.map((c, i) => (
                      <MiniOrb
                        key={`${c}-${i.toString()}`}
                        color={c}
                        z={(i % 4) * 8}
                        delay={0.25 + i * 0.022}
                      />
                    ))}
                    {overflow > 0 && (
                      <Text fontSize="12px" fontWeight={800} color="white" opacity={0.85} ml={1}>
                        +{overflow}
                      </Text>
                    )}
                  </Flex>
                  {porGenero.length > 0 && (
                    <Flex gap={3} mt={2.5} wrap="wrap">
                      {porGenero.map(g => (
                        <Flex key={g.sexo} align="center" gap={1.5}>
                          <Box
                            w="9px"
                            h="9px"
                            borderRadius="full"
                            style={{ background: corGenero(g.sexo) }}
                          />
                          <Text fontSize="11px" color="white" opacity={0.8}>
                            {g.sexo}: <b>{g.qtd}</b>
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
              )}
            </Box>

            {/* Orb do score */}
            {score !== undefined && score !== null && (
              <Flex direction="column" align="center" gap={3} flexShrink={0}>
                <ScoreOrb score={score} classif={classif} />
                <Text
                  fontSize="10px"
                  color="white"
                  opacity={0.6}
                  textAlign="center"
                  maxW="160px"
                  style={{ transform: 'translateZ(40px)' }}
                >
                  Período {periodoLabel}
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>
      </motion.div>
    </Box>
  );
}
